import { CheckCircle2Icon, CircleAlertIcon, Trash2Icon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Button as RACButton } from "react-aria-components";
import {
  Controller,
  type FieldArrayWithId,
  useFormContext,
} from "react-hook-form";

import { Button } from "@/components/button";
import { Checkbox } from "@/components/checkbox";

import type { FileUploadStatus } from "../hooks/useUploadFiles";
import type { UploadFilesFormData } from "../schema";

import { FILE_ICONS } from "../../../assets/file-icons";
import { ConfirmDialog } from "./confirm-dialog";
import { FileCombobox } from "./file-combobox";
import { ProgressSpinner } from "./progress-spinner";

function FileStatus({
  errorMessage,
  onCancel,
  onDelete,
  progress = 0,
  status,
}: {
  errorMessage?: string;
  formIndex: number;
  onCancel?: () => void;
  onDelete?: () => void;
  progress?: number;
  status: FileUploadStatus;
}) {
  const [showCompleteTemporarily, setShowCompleteTemporarily] = useState(false);
  const hasShownCompleteIndicator = useRef(false);

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;

    if (status === "uploaded") {
      if (hasShownCompleteIndicator.current) {
        setShowCompleteTemporarily(false);
      } else {
        setShowCompleteTemporarily(true);
        hasShownCompleteIndicator.current = true;

        timer = setTimeout(() => {
          setShowCompleteTemporarily(false);
        }, 3000);
      }
    } else {
      setShowCompleteTemporarily(false);
      hasShownCompleteIndicator.current = false;
    }

    return () => clearTimeout(timer);
  }, [status]);

  switch (status) {
    case "error": {
      return (
        <div className="flex flex-row gap-[8px] items-center self-start mt-[18px] group max-h-[54px]">
          {errorMessage && (
            <>
              <span className="text-xs font-regular text-text-error text-right hidden group-hover:inline overflow-x-auto">
                {errorMessage || "Upload failed"}
              </span>
              <div className="size-[32px] rounded-full bg-bg-error-secondary grid place-items-center text-featured-icon-light-fg-error min-w-[32px]">
                <CircleAlertIcon size={16} />
              </div>
            </>
          )}
          {onDelete && (
            <RACButton
              className="hover:cursor-pointer size-[36px] grid place-items-center text-fg-error-primary"
              onPress={onDelete}
            >
              <Trash2Icon size={16} />
            </RACButton>
          )}
        </div>
      );
    }
    case "uploaded": {
      return (
        <div className="flex flex-row gap-[8px] items-center self-start mt-[18px]">
          {showCompleteTemporarily ? (
            <>
              <div className="flex flex-col text-xs font-regular text-text-tertiary text-right">
                <span>Upload</span>
                <span>complete</span>
              </div>
              <div className="size-[32px] rounded-full bg-bg-success-secondary grid place-items-center text-featured-icon-light-fg-success">
                <CheckCircle2Icon size={16} />
              </div>
            </>
          ) : onDelete ? (
            <RACButton
              className="hover:cursor-pointer size-[36px] grid place-items-center text-fg-error-primary"
              onPress={onDelete}
            >
              <Trash2Icon size={16} />
            </RACButton>
          ) : (
            <div className="size-[36px]"></div>
          )}
        </div>
      );
    }
    case "uploading": {
      return (
        <div className="flex flex-row gap-[8px] items-center self-start mt-[18px]">
          <div className="flex flex-col text-xs font-regular text-text-tertiary text-right">
            <span>Uploading</span>
            {onCancel && (
              <RACButton
                className="hover:cursor-pointer text-xs text-button-tertiary-color-fg hover:underline"
                onPress={onCancel}
              >
                Tap to cancel
              </RACButton>
            )}
          </div>
          {progress ? (
            <ProgressSpinner progress={progress} size={32} />
          ) : (
            <svg
              className="animate-spin"
              fill="none"
              height="32"
              viewBox="0 0 32 32"
              width="32"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M30 16C30 17.8385 29.6379 19.659 28.9343 21.3576C28.2308 23.0561 27.1995 24.5995 25.8995 25.8995C24.5995 27.1995 23.0561 28.2307 21.3576 28.9343C19.659 29.6379 17.8385 30 16 30C14.1615 30 12.341 29.6379 10.6424 28.9343C8.94387 28.2307 7.40052 27.1995 6.1005 25.8995C4.80048 24.5995 3.76925 23.0561 3.06569 21.3576C2.36212 19.659 2 17.8385 2 16C2 14.1615 2.36212 12.341 3.06569 10.6424C3.76926 8.94387 4.80049 7.40052 6.10051 6.1005C7.40053 4.80048 8.94388 3.76925 10.6424 3.06568C12.341 2.36212 14.1615 2 16 2C17.8385 2 19.659 2.36212 21.3576 3.06569C23.0561 3.76926 24.5995 4.80049 25.8995 6.10051C27.1995 7.40053 28.2308 8.94388 28.9343 10.6424C29.6379 12.341 30 14.1615 30 16L30 16Z"
                stroke="#F2F4F7"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="4"
              />
              <path
                d="M16 2C18.5819 2 21.1135 2.71399 23.315 4.06304C25.5164 5.41208 27.3019 7.34363 28.4741 9.64413C29.6463 11.9446 30.1594 14.5245 29.9568 17.0984C29.7543 19.6724 28.8439 22.1402 27.3262 24.229C25.8086 26.3178 23.7429 27.9463 21.3576 28.9343C18.9722 29.9224 16.36 30.2315 13.8099 29.8276C11.2598 29.4237 8.87103 28.3225 6.90773 26.6457C4.94443 24.9689 3.48306 22.7818 2.68521 20.3262"
                stroke="#6938EF"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="4"
              />
            </svg>
          )}
        </div>
      );
    }
    default: {
      return null;
    }
  }
}

function FileItem({
  field,
  formIndex,
  onCancel,
  onDelete,
}: {
  field: FieldArrayWithId<UploadFilesFormData, "files", "fieldId">;
  formIndex: number;
  onCancel: (index: number) => void;
  onDelete: (index: number) => void;
}) {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const { control, watch } = useFormContext<UploadFilesFormData>();

  const watchedStatus = watch(`files.${formIndex}.status`);
  const watchedErrorMessage = watch(`files.${formIndex}.errorMessage`);
  const watchedUploadProgress = watch(`files.${formIndex}.uploadProgress`);
  const previousVersionId = watch(`files.${formIndex}.previousVersionId`);

  const { originalName } = field;
  const extension = originalName.split(".").pop()?.toLowerCase() || "";
  const IconComponent =
    extension in FILE_ICONS
      ? FILE_ICONS[extension as keyof typeof FILE_ICONS]
      : FILE_ICONS.fallback;

  const canDelete = watchedStatus === "uploaded" || watchedStatus === "error";
  const canCancel = watchedStatus === "uploading";
  const isSourceFileCheckboxDisabled =
    watchedStatus === "uploading" || !!previousVersionId;

  return (
    <>
      <div className="flex flex-col gap-xs w-full">
        <div className="flex flex-row justify-between w-full">
          <div className="flex flex-row gap-xl">
            <div className="flex flex-row gap-[8px]">
              <div className="rounded-md shrink-0 size-[28px] mt-[24px]">
                {IconComponent && <IconComponent className="size-full" />}
              </div>
              <Controller
                control={control}
                name={`files.${formIndex}.name`}
                render={({ field: controllerField, fieldState }) => (
                  <FileCombobox
                    field={controllerField}
                    fieldError={fieldState.error}
                    index={formIndex}
                    label={originalName}
                  />
                )}
              />
            </div>
            <Controller
              control={control}
              name={`files.${formIndex}.isSourceFile`}
              render={({ field: { name, onBlur, onChange, value } }) => (
                <Checkbox
                  className="mt-[27px] p-0 shrink-0 self-start"
                  isDisabled={isSourceFileCheckboxDisabled}
                  isSelected={value}
                  name={name}
                  onBlur={onBlur}
                  onChange={onChange}
                >
                  Source file
                </Checkbox>
              )}
            />
          </div>
          <FileStatus
            errorMessage={watchedErrorMessage}
            formIndex={formIndex}
            onCancel={canCancel ? () => onCancel(formIndex) : undefined}
            onDelete={canDelete ? () => setIsDeleteDialogOpen(true) : undefined}
            progress={watchedUploadProgress}
            status={watchedStatus}
          />
        </div>
      </div>
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onConfirm={() => onDelete(formIndex)}
        onDismiss={() => setIsDeleteDialogOpen(false)}
        variant="delete"
      />
    </>
  );
}

interface FileListDisplayProps {
  fields: FieldArrayWithId<UploadFilesFormData, "files", "fieldId">[];
  isUploading: boolean;
  onCancel: (index: number) => void;
  onDelete: (index: number) => void;
  onDeleteAll: () => void;
}
export function FileListDisplay({
  fields,
  isUploading,
  onCancel,
  onDelete,
  onDeleteAll,
}: FileListDisplayProps) {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  return (
    <>
      <div className="flex flex-col gap-xs w-full ">
        <div className="flex justify-between items-center">
          <span className="text-text-primary font-medium text-md">
            List of uploaded files
          </span>
          {fields.length > 0 && (
            <Button
              className="text-sm"
              color="gray"
              isDisabled={isUploading}
              onPress={() => setIsDeleteDialogOpen(true)}
              variant="tertiary"
            >
              Delete all
            </Button>
          )}
        </div>

        {fields.length === 0 ? (
          <div className="flex h-[314px] text-sm font-medium text-text-quaternary">
            No files uploaded yet
          </div>
        ) : (
          <div className="flex flex-col gap-[12px] overflow-y-auto overflow-x-hidden h-[314px] pr-xs">
            {fields.map((field, index) => (
              <FileItem
                field={field}
                formIndex={index}
                key={field.fieldId}
                onCancel={onCancel}
                onDelete={onDelete}
              />
            ))}
          </div>
        )}
      </div>
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onConfirm={() => {
          setIsDeleteDialogOpen(false);
          onDeleteAll();
        }}
        onDismiss={() => setIsDeleteDialogOpen(false)}
        variant="delete"
      />
    </>
  );
}
